<div class="landing-page">
  <!-- Hero -->
  <section class="relative overflow-hidden bg-[color:var(--background)]">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#6e9bad]/20 blur-3xl"></div>
      <div class="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#aa9072]/15 blur-3xl"></div>
    </div>

    <div class="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      <div class="grid gap-10 lg:grid-cols-2 lg:gap-14 items-center">
        <div class="text-center lg:text-left space-y-6">
          <div class="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--secondary)] px-4 py-2 text-sm font-semibold text-[color:var(--primary)]">
            <lucide-icon name="check-circle-2" class="h-4 w-4"></lucide-icon>
            Where Capital Meets Clarity
          </div>

          <h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[color:var(--foreground)] text-balance">
            A smarter real estate platform
          </h1>

          <p class="text-lg sm:text-xl text-[color:var(--muted-foreground)] leading-relaxed max-w-2xl mx-auto lg:mx-0">
            For developers, investors and property owners to streamline sales, manage assets, and simplify transactions.
          </p>

           <div class="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start pt-2">
            <a *ngIf="(isAuthenticated$ | async) === false" routerLink="/signup" class="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--primary)] px-6 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] shadow-sm transition-colors hover:bg-[color:var(--primary-hover)]">
              Get started
              <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
            </a>
            <a *ngIf="isAuthenticated$ | async" routerLink="/properties" class="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--primary)] px-6 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] shadow-sm transition-colors hover:bg-[color:var(--primary-hover)]">
              View properties
              <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
            </a>
            <a routerLink="/projects" class="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-6 py-3 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)] transition-colors">
              View projects
            </a>
          </div>

          <div class="pt-4 grid grid-cols-2 gap-3 max-w-md mx-auto lg:mx-0">
            <div class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-3 text-left shadow-sm">
              <p class="text-xs uppercase tracking-[0.2em] text-[color:var(--text-subtle)]">Direct</p>
              <p class="mt-1 text-sm font-semibold text-[color:var(--foreground)]">Developers & listings</p>
            </div>
            <div class="rounded-2xl border border-[color:var(--border)] bg-[color:var(--card)] px-4 py-3 text-left shadow-sm">
              <p class="text-xs uppercase tracking-[0.2em] text-[color:var(--text-subtle)]">Flexible</p>
              <p class="mt-1 text-sm font-semibold text-[color:var(--foreground)]">Payment plans</p>
            </div>
          </div>
        </div>

	        <div class="relative">
	          <div class="absolute -inset-6 bg-[#6e9bad]/15 blur-3xl rounded-[40px]"></div>
	          <div class="lux-panel relative overflow-hidden rounded-[32px] border border-[color:var(--border)] bg-[color:var(--card)] shadow-[0_22px_50px_rgba(15,23,42,0.12)]">
	            <img [src]="heroImage" (error)="onHeroImageError('hero')" alt="Modern real estate imagery" class="h-[440px] w-full object-cover" loading="eager" fetchpriority="high" decoding="async" />
	          </div>
	          <div
              class="lux-panel lux-hover absolute -bottom-6 left-6 right-6 rounded-3xl border border-[color:var(--border)] bg-[color:var(--card)]/90 backdrop-blur p-4 shadow-lg cursor-pointer"
              role="button"
              tabindex="0"
              (click)="openSearchOverlay()"
              (keydown.enter)="openSearchOverlay()"
            >
	            <div class="flex items-center gap-3">
	              <div class="h-10 w-10 rounded-2xl bg-[color:var(--secondary)] border border-[color:var(--border)] text-[color:var(--primary)] flex items-center justify-center">
	                <lucide-icon name="map" class="h-5 w-5"></lucide-icon>
	              </div>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-[color:var(--foreground)] truncate">Explore curated locations</p>
                <p class="text-xs text-[color:var(--text-subtle)] truncate">Search by country, city, and investment goals</p>
              </div>
              <div class="ml-auto hidden sm:flex items-center gap-2 text-xs font-semibold text-[color:var(--primary)]">
                <span class="rounded-full bg-[color:var(--secondary)] border border-[color:var(--border)] px-3 py-1">New</span>
                <span class="rounded-full bg-[color:var(--secondary)] border border-[color:var(--border)] px-3 py-1">Premium</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Hero strip -->
  <section class="relative overflow-hidden">
	    <div class="relative h-[56vh] min-h-[420px]">
	      <img [src]="heroStripImage" (error)="onHeroImageError('strip')" alt="Modern property building" class="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
	      <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
	    </div>
		    <div class="absolute bottom-0 left-0 right-0 pb-10 sm:pb-12">
		      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
		        <div class="lux-panel lux-hover max-w-2xl rounded-[28px] border border-[color:var(--border)] bg-[color:var(--card)] p-6 sm:p-8 shadow-[0_22px_50px_rgba(0,0,0,0.28)]">
		          <h2 class="text-2xl sm:text-3xl font-semibold text-[color:var(--foreground)]">Discover premium properties</h2>
		          <p class="mt-2 text-secondary">From luxury residences to investment opportunities, find what fits your goals.</p>
		          <div class="mt-5 flex flex-col sm:flex-row gap-3">
		            <a routerLink="/properties" class="inline-flex items-center justify-center rounded-xl bg-[color:var(--primary)] px-5 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
		              Explore listings
		            </a>
	            <a routerLink="/faq" class="inline-flex items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-3 text-sm font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--hover)]">
	              How it works
	            </a>
	          </div>
	        </div>
	      </div>
	    </div>
	  </section>

	  <!-- Featured -->
	  <section class="bg-[color:var(--background)] border-t border-[color:var(--border)]">
	    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
	      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
	        <div>
	          <h2 class="text-2xl sm:text-3xl font-semibold text-[color:var(--foreground)]">Featured collections</h2>
	          <p class="mt-2 text-secondary">Curated inspiration from nibni’s brand imagery—explore and discover.</p>
	        </div>
	        <a routerLink="/properties" class="text-sm font-semibold text-[color:var(--primary)] hover:underline">View all</a>
	      </div>

		      <div class="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
		        <article
		          *ngFor="let listing of featuredListings; trackBy: trackByListingId"
		          class="lux-panel lux-hover overflow-hidden rounded-3xl bg-[color:var(--card)] border border-[color:var(--border)]"
		        >
	          <div class="relative h-56">
	          <app-image-with-skeleton
	            [candidates]="[listing.imageUrl]"
	            [alt]="listing.imageAlt"
	            aspectClass="h-full"
	          ></app-image-with-skeleton>
	            <div class="absolute left-4 top-4 flex items-center gap-2">
	              <span
	                *ngIf="listing.badge"
	                class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold shadow-sm"
	                [ngClass]="listing.badge === 'New'
	                  ? 'bg-[#6e9bad] text-white border-transparent'
	                  : 'bg-[color:var(--secondary)] text-[color:var(--primary)] border-[color:var(--border)]'"
	              >
	                {{ listing.badge }}
	              </span>
	            </div>
	          </div>

	          <div class="p-5">
	            <p class="text-2xl font-bold tracking-tight text-[color:var(--foreground)]">{{ listing.title }}</p>
	            <p class="mt-2 text-sm font-semibold text-secondary">{{ listing.subtitle }}</p>
	            <p class="mt-3 text-sm text-secondary leading-relaxed">
	              {{ listing.description }}
	            </p>
	            <p class="mt-3 flex items-start gap-2 text-sm text-[color:var(--muted-foreground)] leading-snug">
	              <lucide-icon name="map-pin" class="mt-0.5 h-4 w-4 text-[#6e9bad]"></lucide-icon>
	              <span class="min-w-0">{{ listing.location }}</span>
	            </p>

	            <div class="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[color:var(--muted-foreground)]">
	              <div *ngFor="let item of listing.meta" class="inline-flex items-center gap-2">
	                <lucide-icon [name]="item.icon" class="h-4 w-4 text-[color:var(--text-subtle)]"></lucide-icon>
	                <span class="font-semibold text-[color:var(--muted-foreground)]">{{ item.text }}</span>
	              </div>
	            </div>

	            <a
	              routerLink="/properties"
		              class="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-[color:var(--border)] bg-[color:var(--secondary)] px-4 py-3 text-sm font-semibold text-[color:var(--primary)] transition-colors hover:bg-[color:var(--hover)]"
		            >
		              <lucide-icon name="arrow-right" class="h-5 w-5"></lucide-icon>
		              Explore
		            </a>
	          </div>
	        </article>
      </div>
    </div>
  </section>

  <!-- Why Choose -->
  <section class="bg-[color:var(--background)] border-t border-[color:var(--border)]">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div class="text-center max-w-3xl mx-auto">
        <h2 class="text-2xl sm:text-3xl font-semibold text-[color:var(--foreground)]">Why choose nibni</h2>
        <p class="mt-3 text-[color:var(--muted-foreground)]">A clean experience for buyers, investors, and developers.</p>
      </div>

	      <div class="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
	        <div class="lux-panel lux-hover rounded-3xl border border-[color:var(--border)] bg-[color:var(--card)] p-6 shadow-sm">
	          <div class="h-12 w-12 rounded-2xl bg-[color:var(--secondary)] border border-[color:var(--border)] text-[color:var(--primary)] flex items-center justify-center">
	            <lucide-icon name="building-2" class="h-6 w-6"></lucide-icon>
	          </div>
          <h3 class="mt-5 text-lg font-semibold text-[color:var(--foreground)]">Premium supply</h3>
          <p class="mt-2 text-sm text-[color:var(--muted-foreground)]">Curated listings and projects from verified partners.</p>
        </div>

	        <div class="lux-panel lux-hover rounded-3xl border border-[color:var(--border)] bg-[color:var(--card)] p-6 shadow-sm">
	          <div class="h-12 w-12 rounded-2xl bg-[color:var(--muted)] border border-[color:var(--border)] text-[color:var(--accent)] flex items-center justify-center">
	            <lucide-icon name="users" class="h-6 w-6"></lucide-icon>
	          </div>
          <h3 class="mt-5 text-lg font-semibold text-[color:var(--foreground)]">Direct access</h3>
          <p class="mt-2 text-sm text-[color:var(--muted-foreground)]">Connect directly with verified developers, owners, and partners.</p>
        </div>

	        <div class="lux-panel lux-hover rounded-3xl border border-[color:var(--border)] bg-[color:var(--card)] p-6 shadow-sm">
	          <div class="h-12 w-12 rounded-2xl bg-[color:var(--secondary)] border border-[color:var(--border)] text-[color:var(--primary)] flex items-center justify-center">
	            <lucide-icon name="wallet" class="h-6 w-6"></lucide-icon>
	          </div>
          <h3 class="mt-5 text-lg font-semibold text-[color:var(--foreground)]">Flexible plans</h3>
          <p class="mt-2 text-sm text-[color:var(--muted-foreground)]">Transparent pricing with payment plans per listing.</p>
        </div>

	        <div class="lux-panel lux-hover rounded-3xl border border-[color:var(--border)] bg-[color:var(--card)] p-6 shadow-sm">
	          <div class="h-12 w-12 rounded-2xl bg-[color:var(--muted)] border border-[color:var(--border)] text-[color:var(--accent)] flex items-center justify-center">
	            <lucide-icon name="shield-check" class="h-6 w-6"></lucide-icon>
	          </div>
          <h3 class="mt-5 text-lg font-semibold text-[color:var(--foreground)]">Secure & verified</h3>
          <p class="mt-2 text-sm text-[color:var(--muted-foreground)]">Built-in verification and controlled platform access.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section *ngIf="(isAuthenticated$ | async) === false" class="relative border-t border-[color:var(--border)] overflow-hidden">
	    <div class="absolute inset-0">
	      <img [src]="ctaImage" (error)="onHeroImageError('cta')" alt="Interior design" class="h-full w-full object-cover" loading="lazy" decoding="async" />
	      <div class="absolute inset-0 bg-gradient-to-tr from-black/75 via-black/60 to-[#3a4e49]/45"></div>
	    </div>
	    <div class="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
	      <div class="lux-panel lux-hover max-w-2xl rounded-[32px] border border-[color:var(--border)] bg-[color:var(--card)] p-8 shadow-[0_22px_50px_rgba(0,0,0,0.32)]">
	        <h2 class="text-2xl sm:text-4xl font-semibold text-[color:var(--foreground)]">Ready to find your perfect property?</h2>
	        <p class="mt-4 text-secondary text-lg">
	          Explore and manage real estate investment opportunities with nibni.
        </p>
        <div class="mt-8 flex flex-col sm:flex-row gap-3">
          <a routerLink="/properties" class="inline-flex items-center justify-center rounded-xl bg-[color:var(--primary)] px-6 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
            Get started
          </a>
          <a routerLink="/signup" class="inline-flex items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-6 py-3 text-sm font-semibold text-[color:var(--foreground)] hover:bg-[color:var(--hover)] transition-colors">
            Create account
          </a>
        </div>
      </div>
    </div>
  </section>

  <!-- Search overlay -->
  <ng-container *ngIf="searchOverlayVisible">
    <div class="fixed inset-0 z-50">
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-200"
        [class.opacity-100]="searchOverlayOpen"
        [class.opacity-0]="!searchOverlayOpen"
        (click)="closeSearchOverlay()"
      ></div>

      <div class="landing-search-overlay-scroll absolute inset-0 flex items-start justify-center px-4 py-6 overflow-y-auto overscroll-contain sm:items-center sm:py-10">
        <div
          class="lux-panel w-full max-w-2xl rounded-[32px] border border-[color:var(--border)] bg-[color:var(--card)] shadow-[0_30px_80px_rgba(0,0,0,0.22)] transition-all duration-200"
          [class.opacity-100]="searchOverlayOpen"
          [class.opacity-0]="!searchOverlayOpen"
          [class.translate-y-0]="searchOverlayOpen"
          [class.translate-y-4]="!searchOverlayOpen"
          [class.scale-100]="searchOverlayOpen"
          [class.scale-95]="!searchOverlayOpen"
          (click)="$event.stopPropagation()"
        >
          <div class="flex items-start justify-between gap-4 p-6 border-b border-[color:var(--border)]">
            <div>
              <p class="text-xs uppercase tracking-[0.22em] text-[color:var(--text-subtle)]">Search</p>
              <h3 class="mt-1 text-2xl font-semibold text-[color:var(--foreground)]">Find your next property</h3>
              <p class="mt-2 text-sm text-[color:var(--muted-foreground)]">Country, city, property type, and budget—tailored in seconds.</p>
            </div>
            <button type="button" class="rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] p-2 text-[color:var(--muted-foreground)] hover:bg-[color:var(--hover)] transition" (click)="closeSearchOverlay()">
              <lucide-icon name="x" class="h-5 w-5"></lucide-icon>
              <span class="sr-only">Close</span>
            </button>
          </div>

          <div class="p-6 space-y-5">
            <div class="flex items-center gap-3 rounded-2xl border border-[color:var(--border)] bg-[color:var(--muted)] px-4 py-3">
              <lucide-icon name="search" class="h-5 w-5 text-[color:var(--text-subtle)]"></lucide-icon>
              <input
                type="text"
                [(ngModel)]="searchModel.search"
                placeholder="Try “Dubai”, “beachfront”, or “investment”..."
                class="w-full bg-transparent text-[color:var(--foreground)] placeholder:text-[color:var(--text-subtle)] focus:outline-none"
              />
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="space-y-2 text-sm text-[color:var(--muted-foreground)]">
                Country
                <app-country-selector [(ngModel)]="searchModel.country" [disableAutoDetect]="true"></app-country-selector>
              </label>

              <label class="space-y-2 text-sm text-[color:var(--muted-foreground)]">
                Property Type
                <select [(ngModel)]="searchModel.propertyType" class="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-3 text-[color:var(--foreground)] focus:outline-none focus:border-[color:var(--ring)]">
                  <option *ngFor="let opt of propertyTypeOptions" [value]="opt.value">{{ opt.label }}</option>
                </select>
              </label>
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="space-y-2 text-sm text-[color:var(--muted-foreground)]">
                Bedrooms
                <select [(ngModel)]="searchModel.bedrooms" class="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-3 text-[color:var(--foreground)] focus:outline-none focus:border-[color:var(--ring)]">
                  <option [ngValue]="null">Any</option>
                  <option *ngFor="let n of bedroomOptions" [ngValue]="n">{{ n }}+</option>
                </select>
              </label>

              <label class="space-y-2 text-sm text-[color:var(--muted-foreground)]">
                Bathrooms
                <select [(ngModel)]="searchModel.bathrooms" class="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-3 text-[color:var(--foreground)] focus:outline-none focus:border-[color:var(--ring)]">
                  <option [ngValue]="null">Any</option>
                  <option *ngFor="let n of bathroomOptions" [ngValue]="n">{{ n }}+</option>
                </select>
              </label>
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="space-y-2 text-sm text-[color:var(--muted-foreground)]">
                Min Budget
                <input type="number" [(ngModel)]="searchModel.minPrice" placeholder="0" class="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-3 text-[color:var(--foreground)] placeholder:text-[color:var(--text-subtle)] focus:outline-none focus:border-[color:var(--ring)]" />
              </label>
              <label class="space-y-2 text-sm text-[color:var(--muted-foreground)]">
                Max Budget
                <input type="number" [(ngModel)]="searchModel.maxPrice" placeholder="No limit" class="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-3 py-3 text-[color:var(--foreground)] placeholder:text-[color:var(--text-subtle)] focus:outline-none focus:border-[color:var(--ring)]" />
              </label>
            </div>
          </div>

          <div class="flex flex-col gap-3 p-6 border-t border-[color:var(--border)] sm:flex-row sm:items-center sm:justify-between">
            <button type="button" (click)="closeSearchOverlay()" class="inline-flex items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--card)] px-5 py-3 text-sm font-semibold text-[color:var(--foreground)] transition hover:bg-[color:var(--hover)]">
              Cancel
            </button>
            <button type="button" (click)="submitSearch()" class="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--primary)] px-6 py-3 text-sm font-semibold text-[color:var(--primary-foreground)] transition-colors hover:bg-[color:var(--primary-hover)]">
              <lucide-icon name="arrow-right" class="h-4 w-4"></lucide-icon>
              Search properties
            </button>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
</div>
